import {Injectable} from '@angular/core';
import {OAuthEvent, OAuthService} from 'angular-oauth2-oidc';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {HandleError, HttpErrorHandlerService} from './http-error-handling.service';
import {Observable} from 'rxjs';
import {environment} from '../../environments/environment';
import {catchError, tap} from 'rxjs/operators';
import {ApiClientBaseService} from './api-client-base.service';

let httpHeaders = new HttpHeaders({
  'Content-Type': 'application/json',
  'Authorization': 'my-auth-token'
});

export interface UserDatePair {
  when: string;
  user: string;
}

export interface Adhoc {
  name: string;
  value: string;
}

export interface Identities {
  adhoc?: Adhoc[];
}

export interface Message {
  role: string;
  content: string;
  _id: string;
}

export interface Packet {
  _id: string;
  created: UserDatePair;
  identities: Identities;
  subject: string;
  source: string;
  method: string;
  status: any;
  facts: any;
  __v: number;
}

export interface Prompt {
  _id: string;
  __t: string;
  __v: number;
  asJson: boolean;
  bestOf: number;
  created: UserDatePair;
  deleted: null;
  frequencyPenalty: number;
  maxTokens: number;
  messageTemplate: Message[];
  modelConfig: string;
  name: string;
  presencePenalty: number;
  temperature: number;
  topP: number;
  updated?: UserDatePair;
  map?: {
    prompt: string;
    inputVariables: any[];
  },
  combine?: {
    prompt: string;
    inputVariables: any[];
  }
}

export interface S3Ref {
  bucket: string;
  key: string;
}

export interface ChatResponse {
  role: string;
  text: string;
}

export interface Agent {
  _id: string;
  __v: number;
  created: UserDatePair;
  updated?: UserDatePair;
  deleted?: UserDatePair;
  name: string;
  llm: string; // LLMConfig reference ID
  tools: string[]; // Array of Tool reference IDs
}

export interface LLMBase {
  _id: string;
  __v: number;
  created: UserDatePair;
  updated?: UserDatePair;
  deleted?: UserDatePair;
  name: string;
  environmentKey?: string;
}

/* OpenAI Azure-specific properties */
export interface OpenAIAzure extends LLMBase {
  __t: 'openai-azure';
  maxTokens?: number;
  temperature?: number;
  topP?: number;
  logitBias?: Record<string, number>;
  presencePenalty?: number;
  frequencyPenalty?: number;
}


/* Expand with more LLM implementations as needed */
export type LLMLike = OpenAIAzure;

export interface Tool {
  _id: string;
  __v: number;
  created: UserDatePair;
  updated?: UserDatePair;
  deleted?: UserDatePair;
  name: string;
  description: string;
  category: string;
  codeReference: string;
}


@Injectable({
  providedIn: 'root'
})
export class TreeService extends ApiClientBaseService {
  constructor(oauthService: OAuthService,
              http: HttpClient,
              httpErrorHandler: HttpErrorHandlerService) {
    super(oauthService, http, httpErrorHandler);

    this.uriPrefix = environment.api.tree;
    this.handleError = httpErrorHandler.createHandleError('TreeService');
  }

  listPromptDefinitions(page: number = 0, pageSize: number = 100, sort: string = '', sortDirection: number = 1): Observable<any> {
    return this.get(`/admin/prompt-definitions`, {
      'p': page,
      'ps': pageSize,
      's': sort,
      'sd': sortDirection
    }).pipe(catchError(this.handleError('listPromptDefinitions', [])));
  }

  retrievePromptDefinition(id: string): Observable<any> {
    return this.get(`/admin/prompt-definitions/${id}`)
      .pipe(catchError(this.handleError('retrievePromptDefinition', null)));
  }

  createPromptDefinition(promptDefinition: any): Observable<string> {
    return this.post<string>(`/admin/prompt-definitions`, promptDefinition)
      .pipe(catchError(this.handleError('createPromptDefinition', null)));
  }

  updatePromptDefinition(id: string, promptDefinition: any): Observable<any> {
    return this.put(`/admin/prompt-definitions/${id}`, promptDefinition)
      .pipe(catchError(this.handleError('updatePromptDefinition', null)));
  }

  deletePromptDefinition(id: string): Observable<any> {
    return this.delete(`/admin/prompt-definitions/${id}`)
      .pipe(catchError(this.handleError('deletePromptDefinition', null)));
  }

  listPackets(subject: string, status: string = 'all', page: number = 0, pageSize: number = 10): Observable<{
    items: Packet[],
    total: number
  }> {
    return this.get<{ packets: Packet[], total: number }>(`/packets`, {
      'p': page,
      'ps': pageSize,
      'subject': subject,
      'status': status,
    }).pipe(catchError(this.handleError('listPackets', null)));
  }

  retrievePacket(id: string): Observable<Packet> {
    return this.get<Prompt>(`/packets/${id}`)
      .pipe(catchError(this.handleError('retrievePacket', null)));
  }

  listPrompts(page: number = 0, pageSize: number = 10, sortField: string = null, sortDirection: number = 1): Observable<{
    items: Prompt[],
    total: number
  }> {
    return this.get<{ packets: Packet[], total: number }>(`/admin/prompt-definitions`, {
      'p': page,
      'ps': pageSize,
      's': sortField,
      'sd': sortDirection,
    }).pipe(catchError(this.handleError('listPrompts', null)));
  }

  retrievePrompt(id: string): Observable<Prompt> {
    return this.get<Prompt>(`/admin/prompt-definitions/${id}`)
      .pipe(catchError(this.handleError('retrievePrompt', null)));
  }

  createPrompt(prompt: Prompt): Observable<string> {
    return this.post<string>('/admin/prompt-definitions', prompt)
      .pipe(catchError(this.handleError('retrievePrompt', null)));
  }

  updatePrompt(id: string, prompt: Prompt): Observable<any> {
    return this.put<any>(`/admin/prompt-definitions/${id}`, prompt)
      .pipe(catchError(this.handleError('retrievePrompt', null)));
  }

  startExtractionJob(input: S3Ref, plugin: string, subject: string, identities?: Identities): Observable<string> {
    return this.post<string>(`/extract`, {
      s3: input,
      plugin,
      subject,
      identities,
    }).pipe(catchError(this.handleError('startExtractionJob', null)));
  }

  startAudioToTextJob(input: S3Ref, plugin: string, subject: string, identities?: Identities): Observable<string> {
    return this.post<string>(`/audio-to-text`, {
      s3: input,
      plugin,
      subject,
      identities,
    }).pipe(catchError(this.handleError('startAudioToTextJob', null)));
  }

  retrieveFullText(jobId: string): Observable<{ pages: string[] }> {
    return this.get<{ pages: string[] }>(`/packets/${jobId}/facts/full-text`)
      .pipe(catchError(this.handleError('retrieveFullText', null)));
  }

  retrievePromptResponse(promptName, input): any {
    return this.post<any>(`/prompt/${promptName}`, input)
      .pipe(catchError(this.handleError('retrievePromptResponse', null)));
  }

  uploadFile(file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    return this.postFile<any>(`/upload`, formData)
      .pipe(catchError(this.handleError('uploadFile', null)));
  }

  invokePromptChain(chain: string, subject: string, identities: Identities, prompt: string, file: S3Ref): Observable<string> {
    return this.post<string>('/chains', {
      chain,
      subject,
      identities,
      input: {
        prompt,
        s3: file,
      },
    }).pipe(catchError(this.handleError('invokePromptChain', null)));
  }

  async sendPrompt(agentName: string, data: any) {
    const response = await this.postWithResponse<ChatResponse>(`/agents/${agentName}/chat`, data);

    return {
      ...response.body,
      chatId: response.headers.get('x-tree-session'),
    };
  }

  async sendChatLiteral(agentName: string, message: string, chatId: string = undefined) {
    const response = await this.postWithResponse<ChatResponse>(`/agents/${agentName}/chat`, {
      message,
      chatId,
    });

    return {
      ...response.body,
      chatId: response.headers.get('x-tree-session'),
    };
  }

  /* -------------------- AGENT CRUD -------------------- */

  listAgents(page: number = 0, pageSize: number = 100, sort: string = '', sortDirection: number = 1): Observable<{
    items: Agent[],
    total: number
  }> {
    return this.get<{ agents: Agent[], total: number }>(`/admin/agents`, {
      'p': page,
      'ps': pageSize,
      's': sort,
      'sd': sortDirection
    }).pipe(catchError(this.handleError('listAgents', null)));
  }

  retrieveAgent(id: string): Observable<Agent> {
    return this.get<Agent>(`/admin/agents/${id}`)
      .pipe(catchError(this.handleError('retrieveAgent', null)));
  }

  createAgent(agent: Agent): Observable<string> {
    return this.post<string>(`/admin/agents`, agent)
      .pipe(catchError(this.handleError('createAgent', null)));
  }

  updateAgent(id: string, agent: Partial<Agent>): Observable<any> {
    return this.put(`/admin/agents/${id}`, agent)
      .pipe(catchError(this.handleError('updateAgent', null)));
  }

  deleteAgent(id: string): Observable<any> {
    return this.delete(`/admin/agents/${id}`)
      .pipe(catchError(this.handleError('deleteAgent', null)));
  }

  /* -------------------- LLM CRUD -------------------- */

  listLLMs(page: number = 0, pageSize: number = 100, sort: string = '', sortDirection: number = 1): Observable<{
    items: LLMLike[],
    total: number
  }> {
    return this.get<{ llms: LLMLike[], total: number }>(`/admin/llms`, {
      'p': page,
      'ps': pageSize,
      's': sort,
      'sd': sortDirection
    }).pipe(
      catchError(this.handleError('listLLMs', null))
    );
  }

  retrieveLLM(id: string): Observable<LLMLike> {
    return this.get<LLMLike>(`/admin/llms/${id}`)
      .pipe(
        catchError(this.handleError('retrieveLLM', null))
      );
  }

  createLLM(llm: LLMLike): Observable<string> {
    return this.post<string>(`/admin/llms`, llm)
      .pipe(
        catchError(this.handleError('createLLM', null))
      );
  }

  updateLLM(id: string, llm: Partial<LLMLike>): Observable<any> {
    const {__t, ...updateData} = llm; // Prevent changing discriminator type

    return this.put(`/admin/llms/${id}`, updateData)
      .pipe(
        catchError(this.handleError('updateLLM', null))
      );
  }

  deleteLLM(id: string): Observable<any> {
    return this.delete(`/admin/llms/${id}`)
      .pipe(
        catchError(this.handleError('deleteLLM', null))
      );
  }

  /* -------------------- TOOL CRUD -------------------- */

  listTools(page: number = 0, pageSize: number = 100, sort: string = '', sortDirection: number = 1): Observable<{
    items: Tool[],
    total: number
  }> {
    return this.get<{ tools: Tool[], total: number }>(`/admin/tools`, {
      'p': page,
      'ps': pageSize,
      's': sort,
      'sd': sortDirection
    }).pipe(catchError(this.handleError('listTools', null)));
  }

  retrieveTool(id: string): Observable<Tool> {
    return this.get<Tool>(`/admin/tools/${id}`)
      .pipe(catchError(this.handleError('retrieveTool', null)));
  }

  createTool(tool: Tool): Observable<string> {
    return this.post<string>(`/admin/tools`, tool)
      .pipe(catchError(this.handleError('createTool', null)));
  }

  updateTool(id: string, tool: Partial<Tool>): Observable<any> {
    return this.put(`/admin/tools/${id}`, tool)
      .pipe(catchError(this.handleError('updateTool', null)));
  }

  deleteTool(id: string): Observable<any> {
    return this.delete(`/admin/tools/${id}`)
      .pipe(catchError(this.handleError('deleteTool', null)));
  }
}
